import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Price, PriceInput } from '@artosera/ui';
import { useAvanceArtiste, useRembourserAvanceArtiste } from '../../api/comptesArtistes.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { messageCompte } from './compteUi.js';
import styles from './ComptesArtistes.module.css';

export function AvanceDetailPage() {
  const { id = '' } = useParams();
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const lecture = useAvanceArtiste(galerieSlug, id);
  const a = lecture.data?.avance;
  return (
    <div data-ui="page" className={styles.page}>
      <Link to={`/${galerieSlug}/comptes-artistes`}>{label('titre')}</Link>
      <h1>{label('avance')}</h1>
      {lecture.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
      {lecture.isError && (
        <p role="alert">{messageCompte(lecture.error, t('commun:erreurReseau'))}</p>
      )}
      {a && (
        <>
          <p>
            <Link to={`/${galerieSlug}/artistes/${a.artisteId}`}>{label('artiste')}</Link> ·{' '}
            {label(`natures.${a.nature}`)} · {a.date.slice(0, 10)}
          </p>
          <dl className={styles.totaux}>
            <dt>{label('montant')}</dt>
            <dd>
              <Price cents={a.montantCentimes} lang={langue} />
            </dd>
            <dt>{label('restantDu')}</dt>
            <dd>
              <Price cents={a.restantDuCentimes} lang={langue} />
            </dd>
            <dt>{label('regle')}</dt>
            <dd>{label(`regles.${a.regle}`)}</dd>
            {a.plafondPdb !== undefined && (
              <>
                <dt>{label('plafond')}</dt>
                <dd>{a.plafondPdb / 100} %</dd>
              </>
            )}
          </dl>
          {a.oeuvreId && (
            <p>
              <Link to={`/${galerieSlug}/oeuvres/${a.oeuvreId}`}>{label('oeuvre')}</Link>
            </p>
          )}
          {a.editionId && (
            <p>
              {label('edition')} : {a.editionId}
            </p>
          )}
          {a.fournisseurId && (
            <p>
              <Link to={`/${galerieSlug}/contacts/${a.fournisseurId}`}>{label('fournisseur')}</Link>
            </p>
          )}
          {a.piece && (
            <p>
              {label('piece')} :{' '}
              {a.piece.type === 'document' ? (
                <Link to={`/${galerieSlug}/coffre/${a.piece.id}`}>{a.piece.id}</Link>
              ) : (
                a.piece.id
              )}
            </p>
          )}
          {a.coutProductionId && (
            <p>
              {label('coutProduction')} : {a.coutProductionId}
            </p>
          )}
          <section>
            <h2>{label('imputations')}</h2>
            <ul>
              {a.imputations.map((i) => (
                <li key={i.releveId}>
                  <Link to={`/${galerieSlug}/comptes-artistes/releves/${i.releveId}`}>
                    {label('releve')}
                  </Link>{' '}
                  · {i.le.slice(0, 10)} · <Price cents={i.montantCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('remboursements')}</h2>
            <ul>
              {a.remboursements.map((r) => (
                <li key={r.mouvementId}>
                  {r.le.slice(0, 10)} · <Price cents={r.montantCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          {a.regle === 'hors_ventes' &&
            a.restantDuCentimes > 0 &&
            galerieALaction(galerie, 'artist-accounts:validate') && (
              <RemboursementFormulaire
                key={id}
                galerieSlug={galerieSlug}
                avanceId={id}
                restantDu={a.restantDuCentimes}
              />
            )}
        </>
      )}
    </div>
  );
}
function RemboursementFormulaire({
  galerieSlug,
  avanceId,
  restantDu,
}: {
  galerieSlug: string;
  avanceId: string;
  restantDu: number;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const rembourser = useRembourserAvanceArtiste(galerieSlug);
  const [cle, setCle] = useState(() => crypto.randomUUID());
  const [montant, setMontant] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState('');
  return (
    <section>
      <h2>{label('rembourser')}</h2>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          setErreur('');
          setResultat('');
          void rembourser
            .mutateAsync({ id: avanceId, corps: { montantCentimes: montant, date, cle } })
            .then(() => {
              setMontant(0);
              setCle(crypto.randomUUID());
              setResultat(label('remboursementEnregistre'));
            })
            .catch((e: unknown) => setErreur(messageCompte(e, t('commun:erreurReseau'))));
        }}
      >
        <Field htmlFor="remboursement-montant" label={label('montant')}>
          <PriceInput
            id="remboursement-montant"
            cents={montant}
            onCentsChange={setMontant}
            lang={i18n.language === 'en' ? 'en' : 'fr'}
            required
          />
        </Field>
        <Field htmlFor="remboursement-date" label={label('date')}>
          <Input
            id="remboursement-date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        {resultat && <p role="status">{resultat}</p>}
        <Button
          type="submit"
          loading={rembourser.isPending}
          disabled={montant <= 0 || montant > restantDu}
        >
          {label('rembourser')}
        </Button>
      </form>
    </section>
  );
}
